import {
  SHIP_PRICES,
  SHIP_DEPOSIT,
  SHIPWAY_NEED_DEPOSIT_LIST,
  Shipways,
  Shipopts,
  ShipPayMethods,
  shipMapping
} from './constant'

const SHIP_WAY_DEFAULTS = [
  { key: Shipways.SHIPWAY_IBOX, fareWay: Shipopts.RECEIVER },
  { key: Shipways.SHIPWAY_HILIFE, fareWay: Shipopts.RECEIVER },
  { key: Shipways.SHIPWAY_SEVEN_ELEVEN, fareWay: Shipopts.RECEIVER },
  // 全家店到店已停用，不再產生可選寄送方式
  // { key: Shipways.SHIPWAY_FAMI, fareWay: Shipopts.RECEIVER },
  { key: Shipways.SHIPWAY_HCT, fareWay: Shipopts.RECEIVER },
  { key: Shipways.SHIPWAY_OTHERS, fareWay: Shipopts.RECEIVER, fare: '' },
  { key: Shipways.SHIPWAY_MEET, fareWay: Shipopts.POD, fare: '' },
  { key: Shipways.SHIPWAY_HOMENG, fareWay: Shipopts.POD, fare: '', needOnly: true }
]

/** 押金物流的運費部分（不含押金），如 ibox → 60 */
export function getDepositBaseFee (shipway) {
  if (!SHIPWAY_NEED_DEPOSIT_LIST.includes(shipway)) {
    return null
  }
  const total = SHIP_PRICES[shipway]
  return total == null ? null : total - SHIP_DEPOSIT
}

/** 僅 ibox / hilife / seven_eleven 返回預扣合計（運費 + 押金） */
export function getDepositPreholdTotal (shipway) {
  if (!SHIPWAY_NEED_DEPOSIT_LIST.includes(shipway)) {
    return null
  }
  const total = SHIP_PRICES[shipway]
  return total == null ? null : total
}

/** 押金物流的 UI 標題，如「運費60點+押金20點」 */
export function getDepositFareLabel (shipway) {
  const baseFee = getDepositBaseFee(shipway)
  if (baseFee == null) {
    return null
  }
  return `運費${baseFee}點+押金${SHIP_DEPOSIT}點`
}

export function createDefaultShipWayBundle ({ includeHomeng = false } = {}) {
  return SHIP_WAY_DEFAULTS
    .filter(item => includeHomeng || !item.needOnly)
    .map(({ key, fareWay, fare, needOnly, ...rest }) => ({
      isSelected: false,
      fareWay,
      fare: fare !== undefined ? fare : (SHIP_PRICES[key] != null ? SHIP_PRICES[key] : ''),
      key,
      ...rest
    }))
}

export function createDefaultNeedShipWay () {
  return {
    bundle: createDefaultShipWayBundle({ includeHomeng: true }),
    palmboxInfo: ['', '', ''],
    area: [],
    location: ''
  }
}

export function createDefaultGiftShipWay () {
  return {
    bundle: createDefaultShipWayBundle({ includeHomeng: false }),
    area: [],
    location: ''
  }
}

/** 固定運價物流的當前標準運費（贈送者負擔時不校驗） */
export function getExpectedShipFee (ship) {
  if (!ship || ship.shipopts === Shipopts.SENDER) {
    return null
  }
  if (ship.shipway === Shipways.SHIPWAY_HCT && ship.pay_method === ShipPayMethods.OFFLINE) {
    return null
  }
  const expected = SHIP_PRICES[ship.shipway]
  return expected == null ? null : expected
}

export function isShipFeeOutdated (ship) {
  const expected = getExpectedShipFee(ship)
  if (expected == null) {
    return false
  }
  const fee = Number(ship.shipfee)
  return Number.isFinite(fee) && fee !== expected
}

export function hasOutdatedShipFees (ships = []) {
  return ships.some(isShipFeeOutdated)
}

const OFFLINE_RECEIVER_SHIPWAYS = [
  Shipways.SHIPWAY_FAMI,
  Shipways.SHIPWAY_OTHERS,
  Shipways.SHIPWAY_HOMENG
  // Shipways.SHIPWAY_PALMBOX
]

/** 是否需將舊版寄送設定遷移為索取者付運費 */
export function needsShipMigration (ship) {
  if (!ship) {
    return false
  }
  // momo配送（公益直購）：運費固定由 momo 負擔，不應在重新上架時自動改為索取者付運費
  if (ship.shipway === Shipways.SHIPWAY_DIRECT_PURCHASE) {
    return false
  }
  if (ship.shipopts === Shipopts.SENDER) {
    return true
  }
  if (ship.shipway === Shipways.SHIPWAY_HCT && ship.pay_method === ShipPayMethods.OFFLINE) {
    return true
  }
  if (OFFLINE_RECEIVER_SHIPWAYS.includes(ship.shipway) && ship.shipopts === Shipopts.POD) {
    return true
  }
  return false
}

export function hasShipsNeedingMigration (ships = []) {
  return ships.some(needsShipMigration)
}

/** 重新上架時是否需自動更新（含運費負擔或固定運價） */
export function shipNeedsRelistUpdate (ship) {
  if (!ship) {
    return false
  }
  if (needsShipMigration(ship)) {
    return true
  }
  const migrated = migrateShipForEdit(ship)
  return Number(ship.shipfee) !== Number(migrated.shipfee)
}

export function hasShipsNeedingRelistUpdate (ships = []) {
  return ships.some(shipNeedsRelistUpdate)
}

function getMigratedPayMethodLabel (migrated) {
  if (migrated.shipway === Shipways.SHIPWAY_HCT ||
    [Shipways.SHIPWAY_IBOX, Shipways.SHIPWAY_HILIFE, Shipways.SHIPWAY_SEVEN_ELEVEN].includes(migrated.shipway)) {
    return '獲贈者蘿蔔點數支付'
  }
  if (OFFLINE_RECEIVER_SHIPWAYS.includes(migrated.shipway)) {
    return '匯運費給贈送者'
  }
  return '索取者付運費'
}

function getShipRelistNoticeLine (ship) {
  const wayName = shipMapping.way[ship.shipway] || ship.shipway
  const migrated = migrateShipForEdit(ship)
  const payLabel = getMigratedPayMethodLabel(migrated)
  const parts = []

  if (ship.shipopts === Shipopts.SENDER) {
    parts.push(`贈送者付運費 → ${payLabel}`)
  } else if (ship.shipway === Shipways.SHIPWAY_HCT && ship.pay_method === ShipPayMethods.OFFLINE) {
    parts.push('匯運費給贈送者 → 獲贈者蘿蔔點數支付')
  } else if (OFFLINE_RECEIVER_SHIPWAYS.includes(ship.shipway) && ship.shipopts === Shipopts.POD) {
    parts.push('索取者貨到付款 → 匯運費給贈送者')
  } else if (needsShipMigration(ship)) {
    parts.push(`已更新為${payLabel}`)
  }

  if (Number(ship.shipfee) !== Number(migrated.shipfee)) {
    parts.push(`所需點數 ${ship.shipfee} 點 → ${migrated.shipfee} 點`)
  }

  if (!parts.length) {
    return null
  }
  return `${wayName}：${parts.join('，')}`
}

/** 重新上架時，列出需自動更新的寄送方式說明 */
export function getShipMigrationNoticeLines (ships = []) {
  return ships
    .filter(shipNeedsRelistUpdate)
    .map(getShipRelistNoticeLine)
    .filter(Boolean)
}

/** 前台顯示運費負擔方式（含舊版 pod 遷移） */
export function getShipOptLabel (ship) {
  const migrated = migrateShipForEdit(ship)
  const opts = shipMapping.opts[migrated.pay_method]
  return opts ? (opts[migrated.shipopts] || '') : ''
}

/** 編輯頁載入／重新上架：贈送者付運費改索取者付運費，並同步固定運價 */
export function migrateShipForEdit (ship) {
  if (!ship) {
    return ship
  }
  // momo配送（公益直購）：運費固定由 momo 負擔，不允許任何自動遷移
  if (ship.shipway === Shipways.SHIPWAY_DIRECT_PURCHASE) {
    return ship
  }
  let migrated = { ...ship }

  if (migrated.shipopts === Shipopts.SENDER) {
    migrated = { ...migrated, shipopts: Shipopts.RECEIVER }
  }

  if (migrated.shipway === Shipways.SHIPWAY_HCT) {
    migrated = {
      ...migrated,
      pay_method: ShipPayMethods.ONLINE,
      shipopts: migrated.shipopts === Shipopts.POD ? Shipopts.RECEIVER : migrated.shipopts
    }
  } else if (OFFLINE_RECEIVER_SHIPWAYS.includes(migrated.shipway)) {
    migrated = {
      ...migrated,
      pay_method: ShipPayMethods.OFFLINE,
      shipopts: migrated.shipopts === Shipopts.POD ? Shipopts.RECEIVER : migrated.shipopts
    }
  }

  const expected = getExpectedShipFee(migrated)
  if (expected != null && Number(migrated.shipfee) !== expected) {
    migrated = { ...migrated, shipfee: expected }
  }
  return migrated
}

export function migrateShipsForApi (ships = []) {
  return ships.map(ship => {
    const migrated = migrateShipForEdit(ship)
    return {
      shipway: migrated.shipway,
      shipfee: migrated.shipfee || 0,
      shipopts: migrated.shipopts
    }
  })
}
